import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { CheckSquare, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config/api";
import { format } from "date-fns";


export default function TeamDashboard() {
  const { user, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, getAuthHeader()),
        axios.get(`${API}/tasks`, getAuthHeader()),
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [completingTaskId, setCompletingTaskId] = useState(null);
  
  const handleToggleComplete = async (task) => {
    if (completingTaskId) return; // Prevent double-click
    setCompletingTaskId(task.id);
    try {
      if (task.is_completed) {
        await axios.post(`${API}/tasks/${task.id}/uncomplete`, {}, getAuthHeader());
        toast.success("Sarcină marcată ca nefinalizată");
      } else {
        await axios.post(`${API}/tasks/${task.id}/complete`, {}, getAuthHeader());
        toast.success("Sarcină finalizată! Administratorul a fost notificat.");
      }
      fetchData();
    } catch (error) {
      toast.error("Eroare la actualizarea sarcinii");
    } finally {
      setCompletingTaskId(null);
    }
  };

  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date() && !tasks.find((t) => t.deadline === deadline)?.is_completed;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă dashboard-ul...</div>
      </div>
    );
  }

  const pendingTasks = tasks.filter((t) => !t.is_completed);
  const completedTasks = tasks.filter((t) => t.is_completed);

  return (
    <div className="space-y-6" data-testid="team-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">
          Bine ai venit, {user?.name}
        </h1>
        <p className="font-body text-muted-foreground mt-1">
          Iată rezumatul sarcinilor tale pentru astăzi.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-hover" data-testid="stat-total-tasks">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <CheckSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Total Sarcini</p>
                <p className="font-mono text-2xl font-bold">{stats?.my_tasks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-pending-tasks">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-warning/10">
                <Clock className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">În Așteptare</p>
                <p className="font-mono text-2xl font-bold">{stats?.my_pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-completed-tasks">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-success/10">
                <CheckCircle className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Finalizate</p>
                <p className="font-mono text-2xl font-bold">{stats?.my_completed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Tasks */}
      <Card data-testid="pending-tasks-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Clock className="w-5 h-5 text-warning" />
            Sarcini în Așteptare ({pendingTasks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-3" />
              <p className="font-body text-muted-foreground">Totul la zi! Nu ai sarcini în așteptare.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <div
                  key={task.id}
                  className={`flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-secondary/30 transition-colors ${
                    isOverdue(task.deadline) ? "border-destructive/50" : ""
                  }`}
                  data-testid={`pending-task-${task.id}`}
                >
                  <Checkbox
                    checked={task.is_completed}
                    onCheckedChange={() => handleToggleComplete(task)}
                    disabled={completingTaskId === task.id}
                    className="mt-1"
                    data-testid={`task-checkbox-${task.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-body font-medium">{task.title}</span>
                      {task.case_no && (
                        <Badge variant="outline" className="font-mono text-xs">
                          {task.case_no}
                        </Badge>
                      )}
                      {isOverdue(task.deadline) && (
                        <Badge variant="destructive" className="font-body text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Întârziat
                        </Badge>
                      )}
                    </div>
                    {task.description && (
                      <p className="font-body text-sm text-muted-foreground mt-1">
                        {task.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        Termen limită: {format(new Date(task.deadline), "dd/MM/yyyy")}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleComplete(task)}
                    data-testid={`complete-btn-${task.id}`}
                  >
                    Finalizează
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <Card data-testid="completed-tasks-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              Sarcini Finalizate ({completedTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completedTasks.slice(0, 5).map((task) => (
                <div
                  key={task.id}
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card/50 opacity-75"
                  data-testid={`completed-task-${task.id}`}
                >
                  <Checkbox
                    checked={task.is_completed}
                    onCheckedChange={() => handleToggleComplete(task)}
                    disabled={completingTaskId === task.id}
                    className="mt-1"
                    data-testid={`task-checkbox-completed-${task.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-body font-medium line-through text-muted-foreground">
                      {task.title}
                    </span>
                    {task.completed_at && (
                      <p className="font-mono text-xs text-muted-foreground mt-1">
                        Finalizat: {format(new Date(task.completed_at), "dd/MM/yyyy HH:mm")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
